import { PayloadAction } from '@reduxjs/toolkit';
import { CancelToken } from 'axios';
import { call, put, select } from 'redux-saga/effects';

import { $httpRootApi, getCancelRequestSource } from '@core/http-client';
import { configActions } from '@redux/reducers/config/configReducer';
import { savedViewsCompaniesActions } from '@redux/reducers/saved_views/companies/companiesReducer';
import { savedViewsCompaniesSelectors } from '@redux/selectors/saved_views/companies/savedViewCompanies';
import {
  ManageSavedViewRequestPayload,
  PinUnpinSavedViewRequestPayload,
  SavedViewState,
  UpdateSavedViewRequestPayload,
} from '@redux/types/saved-views/common/savedViewsCommonTypes';
import { showNotification } from '@shared/components/notification';
import { getDefaultError } from '@shared/utils/common';

const getSaveViews = () => {
  return $httpRootApi.get(`/saved-views/crm-companies`);
};

const getSavedViewById = (id: Id) => {
  return $httpRootApi.get(`/saved-views/crm-companies/${id}`);
};

const updatePinnedSavedViews = ({ userId, pinnedIds }: UpdateSavedViewRequestPayload, cancelToken: CancelToken) => {
  return $httpRootApi.put(
    `/users/${userId}/update/pinned_views_crm_companies`,
    {
      pinned_views_crm_companies: pinnedIds,
    },
    { cancelToken }
  );
};

const deleteSavedView = (id: Id) => {
  return $httpRootApi.delete(`/saved-views/${id}`);
};

const editSavedView = (data: ManageSavedViewRequestPayload) => {
  return $httpRootApi.put(`/saved-views/${data.id}`, data);
};

const createSavedView = (data: ManageSavedViewRequestPayload) => {
  return $httpRootApi.post(`/saved-views/crm-companies`, data);
};

export function* getCompaniesSavedViewsSaga() {
  try {
    yield put(savedViewsCompaniesActions.setListLoading(true));

    const { data } = yield call(getSaveViews);

    yield put(savedViewsCompaniesActions.getListSuccess(data.data));
  } catch (e) {
    const errorMessage = getDefaultError(e?.repsponse?.data?.message);

    showNotification(errorMessage, 'error');
    yield put(savedViewsCompaniesActions.getListFailure(errorMessage));
  }
}

export function* getCompaniesSavedViewById(action: PayloadAction<Id>) {
  try {
    yield put(savedViewsCompaniesActions.setUnpinnedViewLoading(true));

    const { data } = yield call(() => getSavedViewById(action.payload));

    yield put(savedViewsCompaniesActions.getUnpinnedViewSuccess(data));
  } catch (e) {
    yield put(savedViewsCompaniesActions.getUnpinnedViewFailure(getDefaultError(e?.response?.data?.message)));
  }
}

export function* updatePinnedCompaniesSavedViewsSaga(action: PayloadAction<UpdateSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewsCompaniesActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedSavedViews(action.payload, source.token));

    yield put(savedViewsCompaniesActions.updateViewsSuccess());
    yield put(configActions.setUser(data));
  } catch (e) {
    yield put(savedViewsCompaniesActions.updateViewsFailure(getDefaultError(e?.response?.data?.message)));
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewsCompaniesSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* pinSavedViewCompanySaga(action: PayloadAction<PinUnpinSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewsCompaniesActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedSavedViews(action.payload, source.token));

    yield put(configActions.setUser(data));
    yield put(
      savedViewsCompaniesActions.pinViewSuccess({
        view: action.payload.view,
        isActiveView: action.payload.isActiveView,
      })
    );
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewsCompaniesSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* unpinSavedViewCompanySaga(action: PayloadAction<PinUnpinSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewsCompaniesActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedSavedViews(action.payload, source.token));

    yield put(configActions.setUser(data));
    yield put(
      savedViewsCompaniesActions.unpinViewSuccess({
        view: action.payload.view,
        isActiveView: action.payload.isActiveView,
      })
    );
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewsCompaniesSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* deleteSavedViewCompaniesSaga(action: PayloadAction<Id>) {
  try {
    yield call(() => deleteSavedView(action.payload));

    yield put(configActions.deleteSavedViewCompaniesSuccess(action.payload));
    yield put(savedViewsCompaniesActions.deleteViewSuccess(action.payload));
  } catch (e) {
    showNotification(getDefaultError(e?.message), 'error');
  }
}

export function* editSavedViewCompaniesSaga(action: PayloadAction<ManageSavedViewRequestPayload>) {
  try {
    const { data } = yield call(() => editSavedView(action.payload));

    yield put(configActions.editSavedViewCompaniesSuccess(data));
    yield put(savedViewsCompaniesActions.editViewSuccess(data));
  } catch (e) {
    showNotification(
      getDefaultError('Sorry, we could not update your view. Please contact us for assistance.'),
      'error'
    );
  }
}

export function* createSavedViewCompaniesSaga(action: PayloadAction<ManageSavedViewRequestPayload>) {
  try {
    const { data } = yield call(() => createSavedView(action.payload));

    yield put(savedViewsCompaniesActions.createViewSuccess(data));
  } catch (e) {
    showNotification(getDefaultError(e?.message), 'error');
  }
}
